// One record an agent's reply links to (a source), as a chip: its kind's icon and its label
// (refKinds), pressed to open it. A record the viewer can't open (no access, gone, or a task they
// aren't on) says only its kind's words ("a fax") and can't be pressed.
// - In the Sources row it is a button: small, on one line, with the whole label in its title; one the
//   viewer can't open is greyed out.
// - In the reply's text (`inline`) it is a span that acts as a button (it takes focus, and Enter or
//   Space presses it, once however long the key is held). A <button> is laid out as one box, so a long
//   label would leave its line whole and break the sentence apart; a span wraps with the sentence. One
//   the viewer can't open is just its kind's words, with a capital when it starts a sentence
//   (`capital`). This is the one place those words are chosen.
//
// Props: item (a ref as the agent detail resolves it for the viewer: {key, kind, label, available,
// mentioned, used}); onOpen(item); inline; capital.
import React from 'react';
import { refKind, refLabel } from './refKinds';

const SHUT_TITLE = "You can't open this record.";

const CHIP = 'inline-flex max-w-full items-baseline gap-1 rounded-md border px-1.5 py-0.5 text-left text-[11px] font-medium leading-4 align-baseline';
const OPEN = 'cursor-pointer border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20';
const SHUT = 'cursor-not-allowed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500';
const INLINE = 'box-decoration-clone cursor-pointer rounded px-0.5 font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20';

const upperFirst = (words) => words.charAt(0).toUpperCase() + words.slice(1);

export default function SourceChip({ item, onOpen = null, inline = false, capital = false }) {
  const kind = refKind(item?.kind);
  if (!kind) return null;
  const label = refLabel(item);
  const open = item.available === true;
  const press = () => onOpen?.(item);
  if (inline) {
    if (!open) return capital ? upperFirst(label) : label;
    const onKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault(); // Space would scroll the page too
      if (event.repeat) return; // a key held down presses once
      press();
    };
    return (
      <span role="button" tabIndex={0} onClick={press} onKeyDown={onKeyDown} className={INLINE}>
        <span aria-hidden="true" className="mr-0.5">{kind.icon}</span>
        {label}
      </span>
    );
  }
  return (
    <button type="button" disabled={!open} title={open ? label : SHUT_TITLE} onClick={open ? press : undefined}
            className={`${CHIP} ${open ? OPEN : SHUT}`}>
      <span aria-hidden="true">{kind.icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
